"use client";

/**
 * P3-15 · Guest recent-search history (localStorage).
 *
 * The signed-in, account-persisted `SearchHistory` path is P3-17; until then a
 * shopper's own recent terms live in localStorage. Per-origin storage is
 * already per-brand (each brand is its own host), so no brand namespacing.
 *
 * Write rule (P3-DB04 §02): terms are added only on submit / option
 * activation — never on keystrokes — deduped (case-insensitive), most-recent
 * first, capped. Whether history persists at all is a brand knob (the overlay
 * passes `enabled`); a disabled brand gets an inert, always-empty list.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "vxolt.recent-searches";
const MAX_RECENT = 10;

function read(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((t): t is string => typeof t === "string").slice(0, MAX_RECENT)
      : [];
  } catch {
    return [];
  }
}

function write(terms: string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(terms));
  } catch {
    // Private mode / quota — recent history is best-effort, never fatal.
  }
}

/** Read the guest localStorage history directly (for the sign-in migration). */
export function readGuestHistory(): string[] {
  return read();
}

/** Clear the guest localStorage history (after migrating it to the account). */
export function clearGuestHistory(): void {
  if (typeof window === "undefined") return;
  write([]);
}

export interface RecentSearches {
  recent: string[];
  add: (term: string) => void;
  remove: (term: string) => void;
  clear: () => void;
}

export function useRecentSearches(enabled = true): RecentSearches {
  const [recent, setRecent] = useState<string[]>([]);

  // Hydrate after mount (SSR renders empty — no localStorage on the server).
  useEffect(() => {
    if (!enabled) return;
    setRecent(read());
    // Reflect writes from another tab so a reopened overlay is never stale.
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setRecent(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [enabled]);

  const add = useCallback(
    (term: string) => {
      if (!enabled) return;
      const value = term.trim();
      if (!value) return;
      setRecent((prev) => {
        const deduped = prev.filter(
          (t) => t.toLowerCase() !== value.toLowerCase(),
        );
        const next = [value, ...deduped].slice(0, MAX_RECENT);
        write(next);
        return next;
      });
    },
    [enabled],
  );

  const remove = useCallback((term: string) => {
    setRecent((prev) => {
      const next = prev.filter((t) => t !== term);
      write(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setRecent([]);
    write([]);
  }, []);

  return { recent, add, remove, clear };
}
